/* eslint-disable react/prop-types */
/**
 * RepairIntegrityCard — iter197aa, RIE Phase 1: Charging System
 * =============================================================
 *
 * Renders the Repair Integrity status banner + Charging System Repair
 * Package overlay above the JG list on the FOH Pricing Builder.
 *
 * Four status indicators (no percentages, per spec):
 *   COMPLETE              — quiet emerald confirmation chip.
 *   ADVISORY              — amber "consider including battery" banner.
 *   WARRANTY_RISK         — red warranty risk banner.
 *   BATTERY_TEST_REQD     — slate "battery test missing" reminder.
 *   (not_applicable → component returns null)
 *
 * Override flow:
 *   • FOH may remove the failed battery from the recommended package,
 *     but only after typing a reason (≥10 chars). The reason is
 *     persisted as an audit row tied to this yestimate.
 *   • The component reads back the most recent active override on
 *     mount so the banner doesn't keep nagging once acknowledged.
 *
 * No JG schema is touched here — this is a SOFT package overlay
 * (per spec choice 2ii).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  ShieldAlert,
  ShieldCheck,
  Battery,
  CircleAlert,
  Info,
  Loader2,
  Check,
} from "lucide-react";

import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { API } from "@/contexts/AuthContext";


const STATUS_META = {
  complete: {
    label:       "Charging System Repair · Complete",
    tone:        "emerald",
    icon:        ShieldCheck,
    testid:      "rie-status-complete",
  },
  advisory: {
    label:       "Charging System Repair · Advisory",
    tone:        "amber",
    icon:        Info,
    testid:      "rie-status-advisory",
  },
  warranty_risk: {
    label:       "Charging System Repair · Warranty Risk",
    tone:        "red",
    icon:        ShieldAlert,
    testid:      "rie-status-warranty-risk",
  },
  battery_test_required: {
    label:       "Charging System Repair · Battery Test Required",
    tone:        "slate",
    icon:        Battery,
    testid:      "rie-status-battery-test-required",
  },
};


const TONE_CLASSES = {
  emerald: {
    bg:     "bg-emerald-50",
    border: "border-emerald-300",
    icon:   "text-emerald-600",
    title:  "text-emerald-900",
    body:   "text-emerald-800",
  },
  amber:   {
    bg:     "bg-amber-50",
    border: "border-amber-300",
    icon:   "text-amber-600",
    title:  "text-amber-900",
    body:   "text-amber-800",
  },
  red:     {
    bg:     "bg-red-50",
    border: "border-red-400",
    icon:   "text-red-600",
    title:  "text-red-900",
    body:   "text-red-800",
  },
  slate:   {
    bg:     "bg-slate-50",
    border: "border-slate-300",
    icon:   "text-slate-600",
    title:  "text-slate-900",
    body:   "text-slate-700",
  },
};


export default function RepairIntegrityCard({ yestimateId, refreshTrigger }) {
  const [data,    setData]    = useState(null);
  const [busy,    setBusy]    = useState(true);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [overrideErr, setOverrideErr] = useState("");

  const load = async () => {
    if (!yestimateId) return;
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/foh/yestimate/${yestimateId}/repair-integrity`,
      );
      setData(r.data || null);
    } catch (e) {
      // 404s mean the yestimate isn't ready yet — quietly stay hidden.
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yestimateId, refreshTrigger]);

  if (busy || !data) return null;
  if (data.status === "not_applicable") return null;

  const meta = STATUS_META[data.status];
  if (!meta) return null;
  const tone = TONE_CLASSES[meta.tone];
  const Icon = meta.icon;

  const primary    = data.primary_label || "charging component";
  const headline   = data.foh_headline || meta.label;
  const overridden = !!data.foh_override;

  const submitOverride = async () => {
    setOverrideErr("");
    const reason = (overrideReason || "").trim();
    if (reason.length < 10) {
      setOverrideErr("Please type at least 10 characters explaining the override.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/foh/yestimate/${yestimateId}/repair-integrity/acknowledge`,
        { reason, action: "remove_battery_from_package" },
      );
      setShowOverride(false);
      setOverrideReason("");
      await load();
    } catch (e) {
      setOverrideErr(
        e?.response?.data?.detail || "Override failed — please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className={`rounded-lg border-2 ${tone.bg} ${tone.border} px-4 py-3 mb-3`}
      data-testid="repair-integrity-card"
      data-status={data.status}
    >
      {/* ---- Header --------------------------------------------------- */}
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${tone.icon} shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className={`text-[13px] font-bold ${tone.title} uppercase tracking-wide`}
              data-testid={meta.testid}
            >
              {meta.label}
            </p>
            {data.battery_test_state && (
              <span
                className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                  data.battery_test_state === "replace"
                    ? "bg-red-200 text-red-900"
                    : data.battery_test_state === "warning"
                      ? "bg-amber-200 text-amber-900"
                      : "bg-emerald-200 text-emerald-900"
                }`}
                data-testid="rie-battery-state-pill"
              >
                Battery · {data.battery_test_state}
              </span>
            )}
            {overridden && (
              <span
                className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-200 text-slate-700"
                data-testid="rie-overridden-pill"
                title={`Acknowledged by ${data.foh_override?.actor_name || "FOH"} — reason: ${data.foh_override?.reason || ""}`}
              >
                FOH override
              </span>
            )}
          </div>
          <p className={`text-[12.5px] ${tone.body} mt-1 leading-snug`}>
            {headline}
          </p>

          {/* Package binding summary — small, calm. */}
          {data.package && (
            <p className={`text-[11px] ${tone.body} mt-1 italic`}>
              Recommended package binds: {data.package.bound_jg_ids.length}
              {" "}line{data.package.bound_jg_ids.length === 1 ? "" : "s"}
              {data.battery_in_estimate ? " · battery already included" : " · battery not yet included"}.
            </p>
          )}
        </div>
      </div>

      {/* ---- Action row — only when actionable + not yet overridden ---- */}
      {(data.status === "advisory" || data.status === "warranty_risk") && !overridden && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            className={`${meta.tone === "red"
              ? "border-red-400 text-red-700 hover:bg-red-100"
              : "border-amber-400 text-amber-800 hover:bg-amber-100"}`}
            disabled
            data-testid="rie-add-battery-to-package"
            title={
              "Add a battery replacement line to the estimate to bring " +
              "this package to Complete state."
            }
          >
            <Battery className="w-3.5 h-3.5 mr-1.5" />
            Add battery to package
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-slate-600 hover:bg-slate-100"
            onClick={() => setShowOverride((v) => !v)}
            data-testid="rie-override-toggle"
          >
            Override · remove from package
          </Button>
        </div>
      )}

      {overridden && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-700">
          <Check className="w-3.5 h-3.5 text-emerald-600" />
          <span>
            FOH override recorded by{" "}
            <strong>{data.foh_override?.actor_name || "FOH"}</strong>
            {data.foh_override?.reason ? ` — "${data.foh_override.reason}"` : ""}.
          </span>
        </div>
      )}

      {/* ---- Override modal (inline panel) ----------------------------- */}
      {showOverride && !overridden && (
        <div
          className="mt-3 rounded border border-slate-300 bg-white px-3 py-2.5"
          data-testid="rie-override-panel"
        >
          <p className="text-[11px] font-semibold text-slate-700 mb-1.5 uppercase tracking-wide flex items-center gap-1.5">
            <CircleAlert className="w-3.5 h-3.5 text-amber-600" />
            Mandatory FOH reason
          </p>
          <p className="text-[11px] text-slate-600 mb-2 italic leading-snug">
            Please record why this {primary} repair is being presented
            without the recommended battery replacement. Stored permanently
            in the audit trail.
          </p>
          <Textarea
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="e.g. Customer replaced battery 2 months ago at another garage — receipt sighted."
            className="text-xs min-h-[60px] bg-white"
            data-testid="rie-override-reason"
          />
          {overrideErr && (
            <p className="text-[11px] text-red-600 mt-1" data-testid="rie-override-error">
              {overrideErr}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Button
              size="sm"
              onClick={submitOverride}
              disabled={submitting}
              className="bg-slate-700 hover:bg-slate-800 text-white"
              data-testid="rie-override-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                  Saving…
                </>
              ) : (
                "Record override"
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowOverride(false);
                setOverrideReason("");
                setOverrideErr("");
              }}
              disabled={submitting}
              data-testid="rie-override-cancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
